import { type FC, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';

// components
import { Link } from 'dibs-elements/exports/Link.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { ExpandableContent } from '../../../components/global/ExpandableContent/ExpandableContent.tsx';

// styles
import styles from './PdpSharedSellerDetailsBio.scss';
import dibsCss from 'dibs-css';
import classnames from 'classnames';

// types
import { type PdpSharedSellerDetailsBio_seller$key as Seller } from './__generated__/PdpSharedSellerDetailsBio_seller.graphql.ts';
import { PdpSharedSellerDetailsModalLazy } from '../PdpSharedSellerDetailsModal/PdpSharedSellerDetailsModalLazy.tsx';

const sellerFragment = graphql`
    fragment PdpSharedSellerDetailsBio_seller on Seller {
        serviceId
        isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
        liveStorefrontProfile {
            aboutUsUnbranded
        }
    }
`;

export const PdpSharedSellerDetailsBio: FC<{
    seller: Seller;
    isCompact?: boolean;
}> = ({ seller: sellerRef, isCompact = false }) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const { isRewardsTier2, liveStorefrontProfile } = seller || {};
    const [isBioModalOpen, setIsBioModalOpen] = useState<boolean>(false);

    const openBioModal = useCallback(() => setIsBioModalOpen(true), []);
    const closeBioModal = useCallback(() => setIsBioModalOpen(false), []);

    const { aboutUsUnbranded: aboutUs } = liveStorefrontProfile || {};

    if (!aboutUs || !isRewardsTier2) {
        return null;
    }

    return (
        <div className={styles.container}>
            <span className={styles.textContainer}>
                {isCompact ? (
                    <ExpandableContent
                        collapseHeight={40}
                        dataTn="pdp-shared-seller-details-bio"
                        buttonClassName={classnames(dibsCss.textBodySmallNormal, dibsCss.mtXxsmall)}
                        scrollTop={false}
                    >
                        <p
                            className={dibsCss.textBodySmallNormal}
                            dangerouslySetInnerHTML={{ __html: aboutUs }}
                        />
                    </ExpandableContent>
                ) : (
                    <p className={styles.text} dangerouslySetInnerHTML={{ __html: aboutUs }} />
                )}
                {!isCompact && (
                    <>
                        <Link className={styles.readMore} onClick={openBioModal}>
                            <FormattedMessage
                                id="PdpSharedSellerDetailsBio.readMore"
                                defaultMessage="Read More"
                            />
                        </Link>
                        <PdpSharedSellerDetailsModalLazy
                            sellerId={seller.serviceId}
                            isOpen={isBioModalOpen}
                            onClose={closeBioModal}
                        />
                    </>
                )}
            </span>
        </div>
    );
};
